import React, { useState, useContext, useEffect } from "react";
import { LanguageContext } from "./LanguageContext";
import "./css/CareersPage.css";

const API_URL = process.env.REACT_APP_API_URL;

const CareersPage = () => {
  const { language } = useContext(LanguageContext);
  const isPT = language === "pt";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null); // { type: "success"|"error", text }

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setResumeFile(file);
    } else if (file) {
      setStatusMsg({
        type: "error",
        text: isPT ? "Por favor, envie um arquivo PDF." : "Please upload a PDF file.",
      });
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg(null);
    setSubmitting(true);

    try {
      let resumeKey = "";

      // Step 1: Upload resume to S3 via presigned URL
      if (resumeFile) {
        const presignRes = await fetch(`${API_URL}/careers/presign`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: resumeFile.name,
            contentType: resumeFile.type,
            fileType: "resume",
          }),
        });

        if (!presignRes.ok) throw new Error("Failed to get upload URL");

        const { uploadUrl, key } = await presignRes.json();
        resumeKey = key;

        const uploadRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": resumeFile.type },
          body: resumeFile,
        });

        if (!uploadRes.ok) throw new Error("Failed to upload resume");
      }

      // Step 2: Submit application
      const applyRes = await fetch(`${API_URL}/careers/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          courseDescription,
          youtubeLink,
          resumeKey,
        }),
      });

      if (!applyRes.ok) throw new Error("Failed to submit application");

      setStatusMsg({
        type: "success",
        text: isPT
          ? "Candidatura enviada com sucesso! Entraremos em contato."
          : "Application submitted successfully! We'll be in touch.",
      });

      // Reset form
      setName("");
      setEmail("");
      setPhone("");
      setCourseDescription("");
      setYoutubeLink("");
      setResumeFile(null);
    } catch (err) {
      console.error("Submit error:", err);
      setStatusMsg({
        type: "error",
        text: isPT
          ? "Algo deu errado. Tente novamente."
          : "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="careers-page">
      <main>
        <h1 className="careers-title">
          {isPT ? (
            <>Carreiras — <span>Ensine Conosco</span></>
          ) : (
            <>Careers — <span>Teach With Us</span></>
          )}
        </h1>

        <section className="careers-info">
          <h2>
            {isPT
              ? "Junte-se ao Nosso Time de Tutores"
              : "Join Our Team of Tutors"}
          </h2>
          <p>
            {isPT
              ? "Estamos procurando educadores apaixonados que querem ajudar pessoas a alcançar a independência financeira. Se você tem experiência em finanças, investimentos ou educação financeira, queremos ouvir de você!"
              : "We're looking for passionate educators who want to help people achieve financial independence. If you have experience in finance, investing, or financial education, we'd love to hear from you!"}
          </p>
          <p>
            {isPT
              ? "Envie seu currículo e uma breve descrição do curso que gostaria de ensinar. Se você tiver vídeos de aulas no YouTube, inclua o link."
              : "Send your resume and a brief description of the course you'd like to teach. If you have sample class videos on YouTube, include the link."}
          </p>
        </section>

        <section className="careers-form-card">
          <h2>{isPT ? "Enviar Candidatura" : "Submit Application"}</h2>

          <form className="careers-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{isPT ? "Nome Completo *" : "Full Name *"}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder={isPT ? "Seu nome completo" : "Your full name"}
              />
            </div>

            <div className="form-group">
              <label>{isPT ? "E-mail *" : "Email Address *"}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={isPT ? "seu@email.com" : "your@email.com"}
              />
            </div>

            <div className="form-group">
              <label>{isPT ? "Telefone" : "Phone Number"}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={isPT ? "(xx) xxxxx-xxxx" : "(xxx) xxx-xxxx"}
              />
            </div>

            <div className="form-group">
              <label>
                {isPT
                  ? "Descreva seu curso ou área de especialidade *"
                  : "Describe your course or area of expertise *"}
              </label>
              <textarea
                value={courseDescription}
                onChange={(e) => setCourseDescription(e.target.value)}
                required
                placeholder={
                  isPT
                    ? "Ex: Gostaria de ensinar um curso sobre análise técnica de ações..."
                    : "E.g., I'd like to teach a course on technical stock analysis..."
                }
              />
            </div>

            <div className="form-group">
              <label>
                {isPT
                  ? "Link do YouTube (aula demonstrativa)"
                  : "YouTube Link (sample class)"}
              </label>
              <input
                type="url"
                value={youtubeLink}
                onChange={(e) => setYoutubeLink(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div className="form-group">
              <label>{isPT ? "Currículo (PDF) *" : "Resume (PDF) *"}</label>
              <div
                className="file-upload-zone"
                onClick={() => document.getElementById("resume-input").click()}
              >
                <input
                  id="resume-input"
                  type="file"
                  accept="application/pdf"
                  onChange={handleResumeChange}
                />
                <p className="upload-label">
                  {resumeFile ? (
                    <span className="file-name">{resumeFile.name}</span>
                  ) : (
                    <>
                      <strong>{isPT ? "Clique para enviar" : "Click to upload"}</strong>
                      {" "}{isPT ? "ou arraste seu PDF" : "or drag your PDF"}
                    </>
                  )}
                </p>
              </div>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !resumeFile}
            >
              {submitting
                ? (isPT ? "Enviando..." : "Submitting...")
                : (isPT ? "Enviar Candidatura" : "Submit Application")}
            </button>
          </form>

          {statusMsg && (
            <div className={`status-message ${statusMsg.type}`}>
              {statusMsg.text}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CareersPage;
